// offline.js — OfflineStore (IndexedDB) + SyncManager (sale write queue + retry)
// ES-Module: exportiert syncManager, getPWAPrompt, clearPWAPrompt.
//
// Architektur:
//   OfflineStore  — rohe IndexedDB-Schicht, kein externes Lib
//   SyncManager   — routet Sale-Writes (create_sale, void_sale),
//                   Background-Retry alle 30 s + bei online-Event

const DB_NAME = "memorica_offline";
const DB_VERSION = 1;

  // ─── OfflineStore ─────────────────────────────────────────────────────────
  class OfflineStore {
    constructor() { this._db = null; }

    async open() {
      if (this._db) return;
      await new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
          const db = e.target.result;
          if (!db.objectStoreNames.contains("pending_actions")) {
            const s = db.createObjectStore("pending_actions", { keyPath: "id" });
            s.createIndex("by_synced", "synced");
          }
          if (!db.objectStoreNames.contains("local_cache")) {
            db.createObjectStore("local_cache", { keyPath: "key" });
          }
        };
        req.onsuccess = (e) => { this._db = e.target.result; resolve(); };
        req.onerror = () => reject(req.error);
      });
    }

    _run(req) {
      return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }

    _store(mode) {
      return this._db.transaction("pending_actions", mode).objectStore("pending_actions");
    }

    async addAction(action) {
      await this.open();
      return this._run(this._store("readwrite").add(action));
    }

    async deleteAction(id) {
      await this.open();
      return this._run(this._store("readwrite").delete(id));
    }

    async getPending() {
      await this.open();
      const all = await this._run(this._store("readonly").getAll());
      return (all || []).filter(a => !a.synced);
    }

    async getPendingCount() {
      const pending = await this.getPending();
      return pending.length;
    }

    async incrementAttempt(id, errorMsg) {
      await this.open();
      const action = await this._run(this._store("readonly").get(id));
      if (!action) return;
      action.sync_attempts = (action.sync_attempts || 0) + 1;
      action.last_attempt_at = new Date().toISOString();
      action.error_message = errorMsg;
      return this._run(this._store("readwrite").put(action));
    }

    async clear() {
      await this.open();
      return this._run(this._store("readwrite").clear());
    }
  }

  // ─── SyncManager ──────────────────────────────────────────────────────────
  class SyncManager {
    constructor(offlineStore) {
      this._store = offlineStore;
      this._getToken = null;
      this.pendingCount = 0;
      this.syncing = false;
      this._listeners = new Set();
      this._retryTimer = null;
      this._onlineHandler = null;
      this._initialized = false;
    }

    // Getter-Funktion für den aktuellen Auth-Token (wird von App gesetzt)
    setTokenGetter(fn) { this._getToken = fn; }

    // Abonniere Statusänderungen: fn(pendingCount, syncing) → unsubscribe
    subscribe(fn) {
      this._listeners.add(fn);
      return () => this._listeners.delete(fn);
    }

    _notify() {
      for (const fn of this._listeners) {
        try { fn(this.pendingCount, this.syncing); } catch {}
      }
    }

    // Einmalig nach Login aufrufen (lädt Pending-Count, startet Retry-Loop)
    async init() {
      if (this._initialized) return;
      this._initialized = true;
      await this._store.open();
      this.pendingCount = await this._store.getPendingCount();
      this._notify();
      this._startRetry();
    }

    // Beim Logout: Queue leeren, Retry-Loop stoppen
    async reset() {
      this._initialized = false;
      if (this._retryTimer) { clearInterval(this._retryTimer); this._retryTimer = null; }
      if (this._onlineHandler) { window.removeEventListener("online", this._onlineHandler); this._onlineHandler = null; }
      await this._store.clear();
      this.pendingCount = 0;
      this.syncing = false;
      this._notify();
    }

    _startRetry() {
      if (this._retryTimer) clearInterval(this._retryTimer);
      this._retryTimer = setInterval(() => this._retryPending(), 30000);
      this._onlineHandler = () => setTimeout(() => this._retryPending(), 1500);
      window.addEventListener("online", this._onlineHandler);
    }

    // Hauptmethode — versucht sofort, queued bei Netzwerkfehler
    // Returns:
    //   { ok: true,  data }                    — Server-Erfolg
    //   { ok: false, queued: true, localId }   — Offline, in Queue
    //   { ok: false, error: string }           — Server-Fehler (nicht retriable)
    async write({ authToken, endpoint, method, payload, action_type, store_id }) {
      try {
        const data = await this._fetch(authToken, endpoint, method, payload);
        return { ok: true, data };
      } catch (e) {
        if (this._isNetworkError(e)) {
          const localId = "pending_" + Date.now() + "_" + Math.random().toString(36).slice(2, 7);
          await this._store.addAction({
            id: localId,
            action_type,
            store_id,
            endpoint,
            method,
            payload,
            token: authToken,
            created_at: new Date().toISOString(),
            synced: false,
            sync_attempts: 0,
            last_attempt_at: null,
            error_message: e.message,
          });
          this.pendingCount = await this._store.getPendingCount();
          this._notify();
          return { ok: false, queued: true, localId };
        }
        return { ok: false, error: e.message };
      }
    }

    async _retryPending() {
      if (this.syncing) return;
      const pending = await this._store.getPending();
      if (pending.length === 0) return;

      this.syncing = true;
      this._notify();

      for (const action of pending) {
        const token = action.token || this._getToken?.();
        if (!token) continue;
        try {
          const data = await this._fetch(token, action.endpoint, action.method, action.payload);
          await this._store.deleteAction(action.id);
          // Aktive Session-Screens über erfolgreiche Synchronisation informieren
          window.dispatchEvent(new CustomEvent("memorica:sale_synced", {
            detail: { action, result: data },
          }));
        } catch (e) {
          if (this._isNetworkError(e)) {
            // Retriable — zähle Fehlversuche
            await this._store.incrementAttempt(action.id, e.message);
          } else {
            // Permanenter Fehler (z.B. 409, 404) — aus Queue entfernen
            await this._store.deleteAction(action.id);
            window.dispatchEvent(new CustomEvent("memorica:sync_error", {
              detail: { action, error: e.message },
            }));
          }
        }
      }

      this.pendingCount = await this._store.getPendingCount();
      this.syncing = false;
      this._notify();
    }

    _isNetworkError(e) {
      if (!(e instanceof TypeError)) return false;
      const msg = e.message.toLowerCase();
      return msg.includes("failed to fetch") ||
             msg.includes("networkerror") ||
             msg.includes("network request failed") ||
             msg.includes("load failed");
    }

    async _fetch(authToken, endpoint, method, payload) {
      const headers = { "Authorization": `Bearer ${authToken}` };
      if (payload !== undefined) headers["Content-Type"] = "application/json";
      const res = await fetch(endpoint, {
        method,
        headers,
        body: payload !== undefined ? JSON.stringify(payload) : undefined,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const err = new Error(`${method} ${endpoint} → ${res.status}: ${text.slice(0, 200)}`);
        err.status = res.status;
        throw err;
      }
      if (res.status === 204) return null;
      return res.json();
    }
  }

// ─── Singleton-Instanzen ──────────────────────────────────────────────────
const _offlineStore = new OfflineStore();
export const syncManager = new SyncManager(_offlineStore);

// ─── PWA-Install-Prompt (Android Chrome / Chromium) ───────────────────────
// beforeinstallprompt feuert früh — wir speichern das Event für später.
let _deferredInstallPrompt = null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  _deferredInstallPrompt = e;
  window.dispatchEvent(new CustomEvent("memorica:pwa_prompt_ready"));
});

export const getPWAPrompt = () => _deferredInstallPrompt;
export const clearPWAPrompt = () => { _deferredInstallPrompt = null; };
